import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Edit, Calendar, Pencil } from "lucide-react";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";
import { FlightBookingProductDetailSkeleton } from "@/components/transactions/FlightBookingProductDetailSkeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogTrigger, DialogContent } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { APIClient, HandleError } from "@/api/axiosClient";
import { RedemptionMerchantApi } from "@/api/redemptionMerchant.api";
import { PlatformCategoryApi } from "@/api/platformCategory.api";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from "@/components/ui/sheet";

const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;

const CURRENCY_OPTIONS = [
  {
    iso3CurrencyCode: "USD",
    currencyName: "United States Dollar",
    currencySymbol: "$",
    id: 1,
  },
  { iso3CurrencyCode: "EUR", currencyName: "Euro", currencySymbol: "€", id: 2 },
  {
    iso3CurrencyCode: "GBP",
    currencyName: "British Pound",
    currencySymbol: "£",
    id: 3,
  },
  {
    iso3CurrencyCode: "INR",
    currencyName: "Indian Rupee",
    currencySymbol: "₹",
    id: 4,
  },
  {
    iso3CurrencyCode: "AED",
    currencyName: "United Arab Emirates Dirham",
    currencySymbol: "د.إ",
    id: 5,
  },
  {
    iso3CurrencyCode: "ETB",
    currencyName: "Ethiopian Birr",
    currencySymbol: "Br",
    id: 6,
  },
];

const FlightBookingProductView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [platformCategories, setPlatformCategories] = useState(null);

  // Edit form state
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    productName: "",
    productCode: "",
    redemptionMerchantId: "",
    fulfilmentType: "",
    loyyalCommissionPercentage: "",
  });
  const [editTermsAndConditions, setEditTermsAndConditions] = useState("");
  const [editFiles, setEditFiles] = useState([]);
  const [editImagePreviews, setEditImagePreviews] = useState<string[]>([]);
  const [editSelectedCategory, setEditSelectedCategory] = useState(null);
  const [editSelectedCurrency, setEditSelectedCurrency] = useState(null);
  const [editSelectedMerchant, setEditSelectedMerchant] = useState(null);

  // Data for dropdowns
  const [merchants, setMerchants] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<number[]>([]);

  useEffect(() => {
    async function fetchProduct() {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE}/product/getGiftCardProduct?productId=${id}`,
          { credentials: "include" }
        );
        const data = await res.json();
        setProduct(data || null);
      } catch (err) {
        toast.error("Failed to fetch product details");
      } finally {
        setLoading(false);
      }
    }
    if (id) fetchProduct();
  }, [id]);

  // Fetch platform categories
  useEffect(() => {
    async function fetchPlatformCategories() {
      try {
        const res = await fetch(
          `${API_BASE}/platformCategory/getPlatformCategoryTree?platformCategoryCode=ROOT`,
          { credentials: "include" }
        );
        const data = await res.json();
        setPlatformCategories(data);
      } catch (err) {
        console.error("Failed to fetch platform categories:", err);
      }
    }
    fetchPlatformCategories();
  }, []);

  // Fetch merchants and categories when component mounts
  useEffect(() => {
    fetchMerchants();
  }, []);

  // Helper functions for edit
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setEditFiles(selectedFiles);
    // Clean up previous previews
    editImagePreviews.forEach((url) => URL.revokeObjectURL(url));
    // Generate previews
    const previews = selectedFiles.map((file) =>
      file instanceof File ? URL.createObjectURL(file) : ""
    );
    setEditImagePreviews(previews);
  };

  const fetchMerchants = async () => {
    setMerchantsLoading(true);
    try {
      const { data } =
        await RedemptionMerchantApi.fetchAllRedemptionMerchants();
      setMerchants(data?.redemptionMerchantVOS || []);
    } catch (err) {
      setMerchants([]);
      toast.error("Failed to fetch merchants");
    } finally {
      setMerchantsLoading(false);
    }
  };

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const { data } = await PlatformCategoryApi.fetchCategoryTree();
      setCategories(data?.subCategoryVOs || []);

      // If we have a selected category ID, find the full category object
      if (editSelectedCategory?.id && !editSelectedCategory.categoryName) {
        const foundCategory = findCategoryById(
          data?.subCategoryVOs || [],
          editSelectedCategory.id
        );
        if (foundCategory) {
          setEditSelectedCategory(foundCategory);
        }
      }
    } catch (err) {
      setCategories([]);
      toast.error("Failed to fetch categories");
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Helper function to find category by ID recursively
  const findCategoryById = (categories, targetId) => {
    for (const category of categories) {
      if (category.id === targetId) {
        return category;
      }
      if (category.subCategoryVOs && category.subCategoryVOs.length > 0) {
        const found = findCategoryById(category.subCategoryVOs, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const formatRelativeTime = (date) => {
    if (!date) return "";
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch {
      return "";
    }
  };

  const getFileUrl = (uri) => {
    if (!uri) return "/placeholder.svg";
    if (uri.startsWith("http")) return uri;
    if (uri.startsWith("/")) {
      return `${API_BASE}${uri}`;
    }
    return `${API_BASE}/file/download?fileUri=${encodeURIComponent(uri)}`;
  };

  // Helper function to find category name by ID
  const findCategoryNameById = (categoryId, categories) => {
    if (!categories || !categoryId) return null;

    // Search in subCategoryVOs recursively
    const searchInCategories = (cats) => {
      for (const cat of cats) {
        if (cat.id === categoryId) {
          return cat.categoryName;
        }
        if (cat.subCategoryVOs && cat.subCategoryVOs.length > 0) {
          const found = searchInCategories(cat.subCategoryVOs);
          if (found) return found;
        }
      }
      return null;
    };

    return searchInCategories(categories.subCategoryVOs || []);
  };

  // Get category names for platform category IDs
  const getCategoryNames = (categoryIds) => {
    if (!categoryIds || !Array.isArray(categoryIds) || !platformCategories) {
      return [];
    }

    return categoryIds
      .map((id) => findCategoryNameById(id, platformCategories))
      .filter((name) => name !== null);
  };

  // Debug: Log product data to understand the structure
  console.log("Flight booking product data:", product);
  console.log("Platform categories:", platformCategories);

  const populateEditForm = () => {
    if (product) {
      setEditForm({
        productName: product.productName || "",
        productCode: product.productCode || "",
        redemptionMerchantId:
          product.redemptionMerchantVO?.id?.toString() || "",
        fulfilmentType: product.fulfilmentType || "",
        loyyalCommissionPercentage:
          product.loyyalCommissionPercentage?.toString() || "",
      });
      setEditTermsAndConditions(product.termsAndConditions || "");
      // Set platform category - will be populated with real data when categories are fetched
      const categoryId = product.platformCategoryIds?.[0];
      setEditSelectedCategory(categoryId ? { id: categoryId } : null);
      setEditSelectedCurrency(
        product.currencyVO
          ? CURRENCY_OPTIONS.find((c) => c.id === product.currencyVO.id)
          : null
      );
      setEditSelectedMerchant(
        product.redemptionMerchantVO
          ? merchants.find((m) => m.id === product.redemptionMerchantVO.id)
          : null
      );
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const updatedFormData = {
        id: product.id, // Include the product ID for update
        productName: editForm?.productName,
        productCode: editForm?.productCode,
        redemptionMerchantVO: { id: editForm?.redemptionMerchantId },
        fulfilmentType: editForm?.fulfilmentType,
        termsAndConditions: editTermsAndConditions,
        productType: "FLIGHT_BOOKING",
        currencyVO: editSelectedCurrency
          ? { id: editSelectedCurrency.id }
          : undefined,
        loyyalCommissionPercentage: editForm?.loyyalCommissionPercentage,
        platformCategoryIds: editSelectedCategory
          ? [editSelectedCategory.id]
          : [],
      };

      const formData = new FormData();
      if (editFiles?.length > 0) {
        editFiles.forEach((file) => {
          formData.append("giftCardProductLogo", file);
        });
      }
      const blob = new Blob([JSON.stringify(updatedFormData)], {
        type: "application/json",
      });
      formData.append("giftCardProductVO", blob);

      await APIClient("/product/createOrUpdateGiftCardProduct", formData, {
        requestType: "post",
        contentType: "multipart/form-data",
      });

      toast.success("Flight booking product updated successfully");
      setDrawerOpen(false);

      // Navigate back to flight booking products screen after successful update
      navigate("/rewardpoint/products/flight-booking");
    } catch (error) {
      toast.error(HandleError(error));
    } finally {
      setEditLoading(false);
    }
  };

  if (loading) return <FlightBookingProductDetailSkeleton />;
  if (!product) return <div className="p-8">Product not found.</div>;

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <button onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </button>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Product Details</h1>
          <p className="text-gray-500">View and manage product information</p>
        </div>
      </div>

      {/* Product header card */}
      <Card className="border-0 shadow-sm overflow-hidden bg-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="h-20 w-20 bg-gray-100 rounded-lg flex items-center justify-center">
                <img
                  src={getFileUrl(product.productImageUri)}
                  alt={product.productName}
                  className="max-h-16 max-w-16 object-contain"
                  onError={(e) => {
                    e.currentTarget.src = "/placeholder.svg";
                  }}
                />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {product.productName}
                </h2>
                <div className="flex items-center gap-3 mt-1 text-gray-500">
                  <span className="text-sm">Code: {product.productCode}</span>
                  {product.createdDate && (
                    <span className="text-sm flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Created around{" "}
                      {formatRelativeTime(product.createdDate)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-base font-medium flex items-center gap-2"
              onClick={async () => {
                populateEditForm();
                // Fetch categories first and set the real category name before opening sheet
                await fetchCategories();
                setDrawerOpen(true);
              }}
            >
              <Edit className="h-4 w-4 mr-1" /> Edit Product
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for different sections */}
      <Tabs defaultValue="details" className="w-full">
        <TabsList className="bg-white border-b border-gray-200 w-full justify-start rounded-none h-12 px-6">
          <TabsTrigger
            value="details"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Flight Booking Details
          </TabsTrigger>
        </TabsList>

        {/* Flight Booking Details Tab */}
        <TabsContent value="details" className="space-y-6 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* General Information */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Product Name
                    </p>
                    <p className="text-gray-800">{product.productName}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Product Code
                    </p>
                    <p className="text-gray-800">{product.productCode}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Merchant
                    </p>
                    <p className="text-gray-800">
                      {product.redemptionMerchantVO?.merchantName || "-"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Flight Booking Details */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Flight Booking Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Fulfilment Type
                    </p>
                    <p className="text-gray-800">
                      {product.fulfilmentType || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Platform Categories
                    </p>
                    <p className="text-gray-800">
                      {product.platformCategoryIds &&
                      product.platformCategoryIds.length > 0
                        ? getCategoryNames(product.platformCategoryIds).join(
                            ", "
                          ) || product.platformCategoryIds.join(", ")
                        : "-"}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Created Date
                    </p>
                    <p className="text-gray-800">
                      {product.createdDate || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Last Updated
                    </p>
                    <p className="text-gray-800">
                      {product.lastUpdatedDate || "-"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing & Terms */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Pricing & Terms
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Currency
                    </p>
                    <p className="text-gray-800">
                      {product.currencyVO?.currencyName || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Loyyal Commission (%)
                    </p>
                    <p className="text-gray-800">
                      {product.loyyalCommissionPercentage || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Terms & Conditions
                    </p>
                    <div className="text-gray-800 prose prose-sm max-w-none">
                      {product.termsAndConditions ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: product.termsAndConditions,
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Product Sheet */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit Flight Booking Product</SheetTitle>
            <SheetDescription>
              Make changes to your flight booking product here. Click save when
              you're done.
            </SheetDescription>
          </SheetHeader>

          <div className="py-6">
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid grid-cols-3 mb-6">
                <TabsTrigger value="general">General Information</TabsTrigger>
                <TabsTrigger value="pricing">Pricing & Terms</TabsTrigger>
                <TabsTrigger value="flight-booking">
                  Flight Booking Details
                </TabsTrigger>
              </TabsList>

              <form onSubmit={handleEditSubmit}>
                {/* General Information Tab */}
                <TabsContent value="general" className="space-y-6">
                  {/* Product Image */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Image
                    </label>
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-24 rounded-lg bg-gray-100 flex items-center justify-center relative border-2 border-dashed border-gray-300">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          id="edit-flight-image-upload"
                          onChange={handleEditFileChange}
                        />
                        <label
                          htmlFor="edit-flight-image-upload"
                          className="cursor-pointer"
                        >
                          {editImagePreviews.length > 0 ? (
                            <img
                              src={editImagePreviews[0]}
                              alt="Product Preview"
                              className="w-20 h-20 object-contain rounded"
                            />
                          ) : product?.productImageUri ? (
                            <img
                              src={getFileUrl(product.productImageUri)}
                              alt="Current Product Image"
                              className="w-20 h-20 object-contain rounded"
                              onError={(e) => {
                                e.currentTarget.src = "/placeholder.svg";
                              }}
                            />
                          ) : (
                            <Plus className="h-8 w-8 text-gray-400" />
                          )}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Product Name*
                      </label>
                      <Input
                        name="productName"
                        placeholder="Enter product name"
                        value={editForm.productName}
                        onChange={handleEditInputChange}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Product Code*
                      </label>
                      <Input
                        name="productCode"
                        placeholder="Enter product code"
                        value={editForm.productCode}
                        onChange={handleEditInputChange}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Merchant*
                      </label>
                      <Select
                        value={editSelectedMerchant?.id?.toString() || ""}
                        onValueChange={(value) => {
                          const merchant = merchants.find(
                            (m) => m.id.toString() === value
                          );
                          setEditSelectedMerchant(merchant);
                          setEditForm((prev) => ({
                            ...prev,
                            redemptionMerchantId: value,
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Merchant" />
                        </SelectTrigger>
                        <SelectContent>
                          {merchantsLoading ? (
                            <div className="px-3 py-2 text-gray-500">
                              Loading...
                            </div>
                          ) : merchants.length === 0 ? (
                            <div className="px-3 py-2 text-gray-500">
                              No merchants
                            </div>
                          ) : (
                            merchants.map((m) => (
                              <SelectItem key={m.id} value={m.id.toString()}>
                                {m.merchantName}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>

                {/* Pricing & Terms Tab */}
                <TabsContent value="pricing" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Currency*
                      </label>
                      <Select
                        value={editSelectedCurrency?.id?.toString() || ""}
                        onValueChange={(value) => {
                          const currency = CURRENCY_OPTIONS.find(
                            (c) => c.id.toString() === value
                          );
                          setEditSelectedCurrency(currency);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Currency" />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCY_OPTIONS.map((opt) => (
                            <SelectItem key={opt.id} value={opt.id.toString()}>
                              {opt.currencyName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Loyyal Commission* (%)
                      </label>
                      <Input
                        name="loyyalCommissionPercentage"
                        placeholder="Enter commission percentage"
                        value={editForm.loyyalCommissionPercentage}
                        onChange={handleEditInputChange}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Terms & Conditions
                    </label>
                    <Textarea
                      placeholder="Enter terms & conditions"
                      rows={5}
                      value={editTermsAndConditions}
                      onChange={(e) =>
                        setEditTermsAndConditions(e.target.value)
                      }
                    />
                  </div>
                </TabsContent>

                {/* Flight Booking Details Tab */}
                <TabsContent value="flight-booking" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fulfilment Type*
                      </label>
                      <Select
                        value={editForm.fulfilmentType}
                        onValueChange={(value) =>
                          setEditForm((prev) => ({
                            ...prev,
                            fulfilmentType: value,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Fulfilment Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="MERCHANT_API">
                            MERCHANT_API
                          </SelectItem>
                          <SelectItem value="MERCHANT_EMAIL">
                            MERCHANT_EMAIL
                          </SelectItem>
                          <SelectItem value="LOYYAL_GENERATED">
                            LOYYAL_GENERATED
                          </SelectItem>
                          <SelectItem value="MERCHANT_UPLOADED">
                            MERCHANT_UPLOADED
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Platform Category
                      </label>
                      <Dialog
                        open={categoryDialogOpen}
                        onOpenChange={setCategoryDialogOpen}
                      >
                        <DialogTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full text-left"
                            onClick={fetchCategories}
                          >
                            {editSelectedCategory
                              ? editSelectedCategory.categoryName
                              : "Select Category"}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md w-full p-6">
                          <div className="rounded-xl border p-6 bg-white">
                            {categoriesLoading ? (
                              <div className="text-center text-gray-500 py-8">
                                Loading...
                              </div>
                            ) : (
                              <div className="flex flex-col gap-2">
                                {categories.map((cat) => {
                                  const isExpanded =
                                    expandedCategoryIds.includes(cat.id);
                                  const hasSub =
                                    cat.subCategoryVOs &&
                                    cat.subCategoryVOs.length > 0;
                                  return (
                                    <div
                                      key={cat.id}
                                      className="flex items-center gap-2"
                                    >
                                      <button
                                        type="button"
                                        className="flex items-center gap-2 flex-1 text-left p-2 rounded hover:bg-gray-100"
                                        onClick={() => {
                                          if (hasSub) {
                                            setExpandedCategoryIds((ids) =>
                                              isExpanded
                                                ? ids.filter(
                                                    (id) => id !== cat.id
                                                  )
                                                : [...ids, cat.id]
                                            );
                                          } else {
                                            setEditSelectedCategory(cat);
                                            setCategoryDialogOpen(false);
                                          }
                                        }}
                                      >
                                        <span className="flex-1">
                                          {cat.categoryName}
                                        </span>
                                        {hasSub && (
                                          <span>{isExpanded ? "▼" : "▶"}</span>
                                        )}
                                        <input
                                          type="radio"
                                          checked={
                                            editSelectedCategory?.id === cat.id
                                          }
                                          onChange={() => {
                                            setEditSelectedCategory(cat);
                                            setCategoryDialogOpen(false);
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          readOnly
                                        />
                                      </button>
                                      {hasSub && isExpanded && (
                                        <div className="ml-4">
                                          {cat.subCategoryVOs.map((subCat) => (
                                            <div
                                              key={subCat.id}
                                              className="flex items-center gap-2"
                                            >
                                              <button
                                                type="button"
                                                className="flex items-center gap-2 flex-1 text-left p-2 rounded hover:bg-gray-100"
                                                onClick={() => {
                                                  setEditSelectedCategory(
                                                    subCat
                                                  );
                                                  setCategoryDialogOpen(false);
                                                }}
                                              >
                                                <span className="flex-1">
                                                  {subCat.categoryName}
                                                </span>
                                                <input
                                                  type="radio"
                                                  checked={
                                                    editSelectedCategory?.id ===
                                                    subCat.id
                                                  }
                                                  onChange={() => {
                                                    setEditSelectedCategory(
                                                      subCat
                                                    );
                                                    setCategoryDialogOpen(
                                                      false
                                                    );
                                                  }}
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                  readOnly
                                                />
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </TabsContent>
              </form>
            </Tabs>
          </div>

          <SheetFooter className="mt-6">
            <SheetClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </SheetClose>
            <Button
              type="submit"
              onClick={handleEditSubmit}
              disabled={editLoading}
            >
              {editLoading ? "Saving..." : "Save changes"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default FlightBookingProductView;
